/**
 * Chat history flyout leaf component for {@link SidebarRail}.
 *
 * Per-row actions (#kebab): hovering/focusing a row reveals a 3-dot button that
 * opens a Pin / Rename / Delete menu. Pin, rename and delete reuse the existing
 * persistence (api.updateConversation for the title/meta, ConversationContext's
 * deleteConversation for removal) and the shared DeleteConfirmDialog + context-
 * menu styling. The full sidebar list has its own equivalent
 * (ConversationItemWithLongPress + ConversationContextMenu); this brings the
 * same actions to the rail History flyout.
 */
import { useMemo, useRef, useState, type MouseEvent } from 'react';
import { EllipsisHorizontalIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import { PinIcon as PinOutlineIcon } from '@sidekickicons/react/24/outline';
import { PinIcon as PinSolidIcon } from '@sidekickicons/react/24/solid';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query/keys';
import { useQueryClient } from '@tanstack/react-query';
import { useSession } from '@/features/auth/hooks/useSession';
import { useConversation } from '@/app/providers/ConversationContext';
import type { Conversation } from '@/types/conversations';
import { isConversationPinned } from '@/types/conversations';
import { DeleteConfirmDialog } from './sidebar/components/DeleteConfirmDialog';
import { useClickOutside } from './sidebar/hooks/useClickOutside';
import { positionMenuBelowButton, CONVERSATION_MENU_WIDTH } from './sidebar/contextMenuGeometry';

// 3 items (Pin/Rename/Delete) — a touch shorter than the sidebar's 4-item menu.
const FLYOUT_MENU_HEIGHT = 148;

/** Chat history flyout */
export function ChatHistoryFlyout({
  sidebarExpanded,
  conversations,
  activeConversationId,
  workspaceId,
  onSelect,
  onClearAll,
  onClose,
}: {
  sidebarExpanded: boolean;
  conversations: Conversation[];
  activeConversationId?: string;
  workspaceId: string;
  onSelect: (conv: Conversation) => void;
  onClearAll: () => void;
  onClose: () => void;
}) {
  const { token } = useSession();
  const { deleteConversation } = useConversation();
  const queryClient = useQueryClient();

  const [searchQuery, setSearchQuery] = useState('');
  const [menu, setMenu] = useState<{ id: string; x: number; y: number } | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Conversation | null>(null);
  const [deletePending, setDeletePending] = useState(false);
  // Optimistic pin/name state so the row re-sorts / shows the new name instantly,
  // before the persisted change round-trips back through the conversations query.
  const [pinOverride, setPinOverride] = useState<Record<string, boolean>>({});
  const [nameOverride, setNameOverride] = useState<Record<string, string>>({});

  const menuRef = useRef<HTMLDivElement | null>(null);
  const nameRefs = useRef<Record<string, HTMLSpanElement | null>>({});

  useClickOutside([menuRef], () => setMenu(null), menu !== null);

  const isPinned = (conv: Conversation) => pinOverride[conv.id] ?? isConversationPinned(conv);
  const displayName = (conv: Conversation) => nameOverride[conv.id] ?? (conv.name || 'Untitled');

  const recency = (c: Conversation) => new Date(c.updatedAt ?? c.createdAt ?? 0).getTime();

  const displayed = useMemo(() => {
    const sorted = [...conversations].sort((a, b) => {
      const ap = isPinned(a);
      const bp = isPinned(b);
      // Pinned section first; existing recency order kept within each group.
      if (ap !== bp) return ap ? -1 : 1;
      return recency(b) - recency(a);
    });
    if (!searchQuery) return sorted;
    const q = searchQuery.toLowerCase();
    return sorted.filter((c) => (c.name ?? '').toLowerCase().includes(q));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversations, searchQuery, pinOverride]);

  const leftOffset = sidebarExpanded ? 220 : 48;

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  const refreshList = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.conversations.all });
  };

  const openMenu = (e: MouseEvent<HTMLElement>, conv: Conversation) => {
    e.stopPropagation();
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const { x, y } = positionMenuBelowButton(rect, CONVERSATION_MENU_WIDTH, FLYOUT_MENU_HEIGHT);
    setMenu((prev) => (prev?.id === conv.id ? null : { id: conv.id, x, y }));
  };

  const handleTogglePin = async (conv: Conversation) => {
    setMenu(null);
    const next = !isPinned(conv);
    setPinOverride((p) => ({ ...p, [conv.id]: next }));
    try {
      await api.workspaces.updateConversation(token ?? null, workspaceId, conv.id, {
        meta: { ...(conv.meta ?? {}), pinned: next },
      });
      refreshList();
    } catch (err) {
      console.error('[ChatHistoryFlyout] Failed to toggle pin:', err);
      setPinOverride((p) => ({ ...p, [conv.id]: !next }));
    }
  };

  const startRename = (conv: Conversation) => {
    setMenu(null);
    setRenamingId(conv.id);
    requestAnimationFrame(() => {
      const el = nameRefs.current[conv.id];
      if (!el) return;
      el.focus();
      const range = document.createRange();
      range.selectNodeContents(el);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
    });
  };

  const saveRename = async (conv: Conversation, el: HTMLElement) => {
    const nextName = (el.textContent ?? '').trim();
    const currentName = displayName(conv);
    setRenamingId(null);
    if (!nextName || nextName === currentName) {
      el.textContent = currentName; // revert any whitespace-only edit
      return;
    }
    setNameOverride((o) => ({ ...o, [conv.id]: nextName })); // show the new name instantly
    try {
      // A non-placeholder title makes the API mark titleSource='user' + locked,
      // so the auto-titler never overwrites a user-chosen name.
      await api.workspaces.updateConversation(token ?? null, workspaceId, conv.id, { title: nextName });
      refreshList();
    } catch (err) {
      console.error('[ChatHistoryFlyout] Failed to rename:', err);
      setNameOverride((o) => { const next = { ...o }; delete next[conv.id]; return next; });
      el.textContent = currentName;
    }
  };

  const requestDelete = (conv: Conversation) => {
    setMenu(null);
    setDeleteTarget(conv);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeletePending(true);
    try {
      await deleteConversation(deleteTarget.id);
      setDeleteTarget(null);
    } catch (err) {
      console.error('[ChatHistoryFlyout] Failed to delete:', err);
    } finally {
      setDeletePending(false);
    }
  };

  const menuConv = menu ? conversations.find((c) => c.id === menu.id) ?? null : null;

  return (
    <>
      <div className="flyout-backdrop" onClick={onClose} />
      <div className="notebooks-flyout open" style={{ left: `${leftOffset}px` }}>
        <div className="flyout-header">
          <span className="flyout-title">HISTORY</span>
          {conversations.length > 0 && (
            <button className="flyout-add-btn" onClick={onClearAll} title="Clear all">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                <path d="M10 11v6" />
                <path d="M14 11v6" />
              </svg>
            </button>
          )}
        </div>

        {conversations.length > 3 && (
          <input
            className="flyout-search"
            type="text"
            placeholder="Search chats…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        )}

        <div className="flyout-list xv-scroll">
          {displayed.length > 0 ? (
            displayed.map((conv) => {
              const pinned = isPinned(conv);
              const renaming = renamingId === conv.id;
              const menuOpen = menu?.id === conv.id;
              return (
                <div
                  key={conv.id}
                  role="button"
                  tabIndex={0}
                  className={`flyout-item${conv.id === activeConversationId ? ' active' : ''}${menuOpen ? ' actions-open' : ''}${renaming ? ' renaming' : ''}`}
                  onClick={() => {
                    if (renaming) return;
                    if (menu) { setMenu(null); return; }
                    onSelect(conv);
                  }}
                  onKeyDown={(e) => {
                    if (renaming) return;
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(conv); }
                  }}
                >
                  <span
                    ref={(el) => { nameRefs.current[conv.id] = el; }}
                    className="flyout-item-name"
                    contentEditable={renaming}
                    suppressContentEditableWarning
                    spellCheck={false}
                    onClick={renaming ? (e) => e.stopPropagation() : undefined}
                    onBlur={renaming ? (e) => saveRename(conv, e.currentTarget) : undefined}
                    onKeyDown={renaming ? (e) => {
                      e.stopPropagation();
                      if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
                      else if (e.key === 'Escape') { e.currentTarget.textContent = displayName(conv); setRenamingId(null); }
                    } : undefined}
                  >
                    {displayName(conv)}
                  </span>
                  {!renaming && (
                    <span className="flyout-item-date">{formatDate(conv.updatedAt ?? conv.createdAt ?? '')}</span>
                  )}
                  {pinned && !renaming && (
                    <PinSolidIcon className="flyout-item-pin-icon" aria-hidden="true" />
                  )}
                  {!renaming && (
                    <button
                      className={`flyout-item-actions-btn${menuOpen ? ' open' : ''}`}
                      onClick={(e) => openMenu(e, conv)}
                      title="Conversation actions"
                      aria-label={`Conversation actions for ${displayName(conv)}`}
                    >
                      <EllipsisHorizontalIcon className="flyout-item-actions-icon" aria-hidden="true" />
                    </button>
                  )}
                </div>
              );
            })
          ) : (
            <div style={{ padding: '12px 14px', fontSize: 12, color: 'var(--rail-muted)' }}>
              {searchQuery ? 'No results' : 'No conversations yet'}
            </div>
          )}
        </div>
      </div>

      {menu && menuConv && (
        <div
          ref={menuRef}
          className="conversation-context-menu"
          style={{ position: 'fixed', top: menu.y, left: menu.x, zIndex: 10000 }}
        >
          <button className="context-menu-item" onClick={() => handleTogglePin(menuConv)}>
            <PinOutlineIcon className="context-menu-item-icon" aria-hidden="true" />
            {isPinned(menuConv) ? 'Unpin' : 'Pin'}
          </button>
          <button className="context-menu-item" onClick={() => startRename(menuConv)}>
            <PencilSquareIcon className="context-menu-item-icon" aria-hidden="true" />
            Rename
          </button>
          <button className="context-menu-item context-menu-item-danger" onClick={() => requestDelete(menuConv)}>
            <TrashIcon className="context-menu-item-icon" aria-hidden="true" />
            Delete
          </button>
        </div>
      )}

      {deleteTarget && (
        <DeleteConfirmDialog
          title="Delete chat?"
          targetName={displayName(deleteTarget)}
          isPending={deletePending}
          onClose={() => setDeleteTarget(null)}
          onConfirm={confirmDelete}
        />
      )}
    </>
  );
}
